import type { DistrictMap } from './locate';
import type { Leadership } from './format';

// The published files, exactly as worker/sr/jobs/build_grid.py writes them.
// The site reads these and nothing else: no Supabase, no key, no third party.

export type Mark =
  | 'Y' | 'N' | 'P' | '-' | '~' | '.' | 'x' | 'o' | 's' | '?' | 'S' | 'V' | 'L';

export type Group = 'bills' | 'budget' | 'noms';
export type Band = 'executive' | 'senate' | 'house';
export type ChamberStatus = 'roll_call' | 'voice' | 'pending' | 'na';
// 'not_yet': a bill still moving in the current Congress, drawn hatched like 'na' but able to
// reach the President; 'na': it never goes there (a resolution, a failed bill, an ended Congress).
export type PresidentStatus = 'signed' | 'vetoed' | 'without_signature' | 'pending' | 'not_yet' | 'na' | 'unknown';

export interface Term {
  congress: number;
  label: string;           // "2025–2026"
  current: boolean;
  grid: string;            // "grid/119-<hash>.json"
  districts?: string;      // "districts/119-<hash>.json": the map behind "Use my location"
  rows: number;
  people?: number;         // rows counts a person who held two offices twice; from 13 Oct 2026
  columns: number;
}

/** How many people a term has: one each, whatever offices they held in it. */
export const peopleIn = (t: Term) => t.people ?? t.rows;

export interface Meta {
  format: number;
  built_at: string;
  default: number;
  terms: Term[];
  sources_updated: Record<string, string>;
  scans_live?: boolean;     // T18: approved scans are published, so How this works describes the new method
  versions_live?: boolean;  // Check B: approved comparisons of versions are published, and How this works says how
}

export interface Column {
  id: string;
  number: string;          // "H.R. 1"
  title: string | null;
  aka?: string[];          // its other names in the record, for search: popular titles, other versions' and parts' short titles
  kind: string;
  group: Group;
  outcome: string;
  date: string;            // the latest final vote, YYYY-MM-DD
  house: ChamberStatus;
  senate: ChamberStatus;
  president: PresidentStatus;
  how?: Partial<Record<'house' | 'senate', string | null>>;   // a chamber that passed it without a roll call
  dates?: Record<'house' | 'senate' | 'president', string | null>;   // each office's own day (from 23 Sep 2026)
  trial?: 'senate';        // a Senate impeachment trial's verdict: Yea is guilty, Nay not guilty (15 Oct 2026)
}

export interface Attendance {
  eligible: number;
  missed: number;
  not_listed: number;
}

export interface Row {
  id: string;
  role: 'president' | 'vp' | 'sen' | 'rep';
  band: Band;
  left: boolean;
  last: string;
  first: string;
  full: string;
  party: string | null;
  seat: string | null;     // "IA", "CA-12", "AK-AL"
  from: string;
  left_on: string | null;
  tenure_days: number;
  years: number;
  first_sworn: string | null;
  gap: boolean;
  next_election: number | null;
  attendance: Attendance | null;
  leader?: Leadership;     // on the Speaker's and the Senate Majority Leader's rows only
}

export interface Grid {
  format: number;
  congress: number;
  label: string;
  current: boolean;
  as_of: string;
  tenure_scale_years: number;
  legend: Record<Mark, string>;
  columns: Column[];
  rows: Row[];
  cells: string[];         // one string per row, one character per column
  delegates: { id: string; full: string; seat: string }[];
}

export interface Tally {
  id: string;
  question: string;
  title: string;
  result: string;
  yea: number | null;
  nay: number | null;
  present: number | null;
  not_voting: number | null;
  source: string;
}

/** A provision an AI summary line cites: its label ("Sec. 101") and its place in the text. */
export type Cite = { label: string; href: string | null };

export interface ItemDoc {
  id: string;
  congress: number;
  number: string;
  kind: string;
  group: Group;
  title: string | null;
  title_source: string | null;
  official_title: string | null;
  titles: { type: string; title: string }[];
  outcome: string;
  law_number: string | null;
  introduced: string | null;
  // `how` and `words` only where a chamber passed it without a roll call (status 'voice'), and only
  // in files built from 23 Sep 2026: the passage action's own words, and how they say it passed.
  // `override`: the final vote is on overriding a veto, and `passage` the chamber's vote passing it before.
  chambers: Record<'house' | 'senate', { status: ChamberStatus; date: string | null; vote: Tally | null;
    how?: string | null; words?: string | null; override?: boolean;
    // a passage with no roll call (H.R. 131's) has no tally or result: `how` and `words` say how it passed
    passage?: (Omit<ItemDoc['other_votes'][number], 'result'> & { result: string | null; how?: string | null; words?: string | null }) | null }>;
  president: { status: PresidentStatus; date: string | null };
  vice_president_tie_breaker: string | null;
  latest_action: { text: string | null; date: string | null };
  card?: ShareCard | null;
  nominees: { pn: string; title: string }[] | null;
  amendments: {
    number: string; sponsor: string | null; purpose: string | null; result: string; date: string;
    yea: number | null; nay: number | null; present: number | null; not_voting: number | null; source: string;
  }[];
  other_votes: {
    chamber: string; date: string; question: string; title: string; result: string; kind: string;
    yea: number | null; nay: number | null; present: number | null; not_voting: number | null; source: string;
  }[];
  // The Congressional Research Service's summary, as plain text blocks (worker/sr/crs.py):
  // h = a heading line, p = a paragraph, li = a list item at depth d. Never HTML.
  crs_summary: {
    date: string | null; stage: string | null; words: number;
    blocks: { k: 'h' | 'p' | 'li'; t: string; d?: number }[];
  } | null;
  // The AI summary, once the review desk has approved it (T14), else null (worker/sr/jobs/
  // build_grid.py, _summary). Every line carries the provision it came from, linked to that
  // provision in the official text on Congress.gov.
  // Since T18 (29 Sep 2026) an approved SCAN takes its place, item by item (build_grid._scan): the
  // same main subject and lines, the core areas, and `unexpected` in place of `unrelated`.
  summary: null | {
    main_subject: { text: string; cites: Cite[] };
    what_it_does: { text: string; cite: Cite }[];
    unrelated?: { text: string; cite: Cite; dollar_amount: string | null }[];
    core_areas?: string[];
    unexpected?: {
      category: string;         // format.ts UNEXPECTED names it
      // added_after: in a section the final text added whole, after the first chamber passed it (Check B)
      items: { cite: Cite; heading: string | null; effect: string; amount: string | null; quote: string; added_after?: boolean }[];
    }[];
    version: { type: string | null; date: string | null; rule: 'enrolled' | 'passed' | 'nearest' | null; href: string | null };
    current: boolean;           // false: the bill has moved on to a newer text since
    model: string | null;
    approved: string | null;    // the day the desk approved it, or the checks published it
    approved_by?: 'desk' | 'checks' | null;   // a scan: Dan read it, or it was published after the checks alone
  };
  // Check B (worker/sr/jobs/build_grid.py, _changes): for a bill one chamber passed and the other changed before
  // the final votes, what the first-passed text had that the final does not, and the reverse. Absent otherwise.
  changes?: {
    first: { chamber: 'House' | 'Senate'; type: string | null; date: string | null; href: string | null };
    final: { type: string | null; date: string | null; href: string | null };
    kept: number | null;        // the share of the first-passed wording that stands in the final text
    replaced: boolean;          // the second chamber replaced the whole text: `added` is empty, `added_count` says
    removed: ChangeRow[];
    added: ChangeRow[];
    added_count: number;
    approved: string | null;
    approved_by: 'desk' | 'checks' | null;
  };
  sources: { congress_gov: string | null; text: string | null };
}

// A section in one text and not the other. `partly`: a section of the OTHER text carries some of it; `part`:
// what it does that the other text does not (null when that was not said). `effect` is null where no AI read it.
export interface ChangeRow { cite: Cite; heading: string | null; effect: string | null; partly: Cite | null; part: string | null }

/** The item's or member's 1200 x 630 link-preview picture (worker sr/cards.py, Phase 6): its address under
 *  /data/, a version of its contents, and its description. Null when the build drew none. */
export interface ShareCard { image: string; v: string; alt: string }

export interface MemberDoc {
  id: string;
  last: string;
  first: string;
  full: string;
  card?: ShareCard | null;
  first_sworn: string | null;
  // `former`: no longer in office, so no contact is published (an office's numbers are its holder's)
  contact: { phone: string | null; form: string | null; former?: boolean };
  // held_open: the dataset's end has come, the member still serving until a special election's winner is seated
  terms: { office: string; start: string; end: string; state: string | null; district: number | null; party: string | null;
           held_open?: boolean }[];
  memberships: {
    congress: number; role: string; chamber: string; party: string | null; seat: string | null;
    from: string; to: string; left: boolean; tenure_days: number; years: number;
    next_election: number | null; attendance: Attendance | null;
    // after the election that fills the term (grid.after_the_election): the term's last day, or the day of the
    // special election whose winner takes an appointee's seat
    term_ends?: string; awaiting_successor?: string;
    leader?: Leadership;
  }[];
}

// The public Corrections log (worker/sr/corrections.py), newest first. Each record's label
// is looked up by the build from the files it published, so every link leads somewhere.
export interface CorrectionEntry {
  id: string;
  corrected_on: string;    // YYYY-MM-DD
  before_launch: boolean;
  records: { kind: 'item' | 'member'; id: string; label: string }[];
  scope: string | null;    // what the fix covered, when it names no single record
  what_changed: string;
  sources: { label: string; url: string }[];
  // Only on an entry whose wording was corrected: each wording it replaced, oldest first, with
  // the day it was replaced and why (worker/sr/corrections.py; never quietly).
  earlier?: { replaced_on: string; why: string; scope: string | null; what_changed: string;
              sources: { label: string; url: string }[] }[];
}

export interface Corrections {
  format: number;
  entries: CorrectionEntry[];
}

const BASE = '/data/';

async function get<T>(path: string): Promise<T> {
  const r = await fetch(BASE + path, { cache: path === 'meta.json' ? 'no-store' : 'default' });
  if (!r.ok) throw new Error(`${path}: ${r.status}`);
  return r.json() as Promise<T>;
}

const grids = new Map<string, Promise<Grid>>();

export const loadMeta = () => get<Meta>('meta.json');
export const loadGrid = (path: string) => {
  if (!grids.has(path)) grids.set(path, get<Grid>(path));
  return grids.get(path)!;
};
/** An item whose vote now belongs to another item's column (a House resolution that only concurred in a
 *  bill's Senate amendment, 12 Oct 2026) is published as a pointer, `moved_to`: that item is read instead,
 *  and a screen showing it puts that item's address in place of the old one. */
export const loadItem = async (id: string): Promise<ItemDoc> => {
  const doc = await get<ItemDoc & { moved_to?: string }>(`item/${id}.json`);
  return doc.moved_to ? get<ItemDoc>(`item/${doc.moved_to}.json`) : doc;
};
export const loadMember = (id: string) => get<MemberDoc>(`member/${id}.json`);
export const loadCorrections = () => get<Corrections>('corrections.json');
const maps = new Map<string, Promise<DistrictMap>>();
export const loadDistrictMap = (path: string) => {
  if (!maps.has(path)) maps.set(path, get<DistrictMap>(path).catch((e) => { maps.delete(path); throw e; }));
  return maps.get(path)!;
};
